import { ref, computed, watch, onMounted } from 'vue';
import { GET_OVERLAY_NAME, GET_ATTRS } from '#shared/types/getters';
import { LINK_RAIL_OVERLAY } from '#shared/constants/common/overlayNames';
import { onUserInit } from '~/events';
import useProvideFactory from '~/composables/dumb/useProvideFactory';

export const SIGN_IN_TOOLTIP_REPOSITORY = Symbol('SIGN_IN_TOOLTIP_REPOSITORY');

export function getSignInTooltipSharedState() {
  return ref(false);
}

export function useSignInTooltipProvide(useAsInject) {
  return useProvideFactory({
    key: SIGN_IN_TOOLTIP_REPOSITORY,
    factory: getSignInTooltipSharedState,
    useAsInject,
  });
}

export function useSignInTooltipActivator({ isSignedIn, isDomestic }) {
  const isActive = useSignInTooltipProvide(true);
  const navHeader = useNavHeader();

  function deactivate() {
    isActive.value = false;
  }

  onUserInit(() => {
    const attrs = navHeader?.[GET_ATTRS];
    const isTooltipWasDisplayed = sessionStorage.getItem('signInTooltipWasDisplayed');

    if (isSignedIn.value && !isTooltipWasDisplayed) {
      sessionStorage.setItem('signInTooltipWasDisplayed', 'true');
    }

    isActive.value = isDomestic.value && !isSignedIn.value && (attrs?.isPermanentSigninTooltip || !isTooltipWasDisplayed);
  });

  return { isSignInTooltipActive: isActive, deactivateSignInTooltip: deactivate };
}

export function useSignInTooltip() {
  const navHeader = useNavHeader();
  const navOverlay = useNavOverlay();

  const isActive = useSignInTooltipProvide(true);
  const overlayName = computed(() => navOverlay?.[GET_OVERLAY_NAME]);
  const attrs = navHeader?.[GET_ATTRS];

  const isTransparent = ref(false);

  let timer = null;
  let startTime = null;
  let timeRemaining = 8000;

  function deactivate() {
    isActive.value = false;
  }

  function startTimer(time) {
    if (attrs?.isPermanentSigninTooltip) return;

    startTime = new Date();
    timer = setTimeout(deactivate, time);
  }

  function suspendTimer() {
    if (attrs?.isPermanentSigninTooltip) return;

    clearTimeout(timer);
    const timeElapsed = new Date() - startTime;
    timeRemaining -= timeElapsed;
  }

  function resumeTimer() {
    startTimer(timeRemaining);
  }

  function storeWatchState() {
    sessionStorage.setItem('signInTooltipWasDisplayed', true);
  }

  onMounted(() => {
    startTimer(timeRemaining);
    storeWatchState();
  });

  watch(overlayName, (cur, prev) => {
    if (attrs?.isPermanentSigninTooltip) {
      if (prev === LINK_RAIL_OVERLAY) {
        isTransparent.value = false;
      } else if (cur === LINK_RAIL_OVERLAY) {
        isTransparent.value = true;
      }
      return;
    }

    if (cur === LINK_RAIL_OVERLAY) {
      deactivate();
    }
  });

  return { isTransparent, suspendTimer, resumeTimer };
}
